import { useEffect, useRef, useState } from 'react';
import {
  api,
  ManualReadingOcrExtraction,
  ManualReadingSubmitBody,
  MeterSearchMatch,
} from '../../api/client';
import { useFieldRole } from '../../hooks/useFieldRole';
import { getBridgeStatus, tapAndAudit, NfcBridgeStatus } from '../../lib/nfcBridge';
import { Alert } from '../../components/ui';

type CaptureSource = 'ocr' | 'nfc';

type Stage =
  | { kind: 'idle' }
  | { kind: 'extracting'; source: CaptureSource }
  | { kind: 'lookup'; source: CaptureSource; extraction: ManualReadingOcrExtraction; image_id: string; image_r2_key: string; capturedReadings: Record<string, number> }
  | { kind: 'no_match'; serial: string; extraction: ManualReadingOcrExtraction; image_id: string; image_r2_key: string }
  | { kind: 'pick_match'; serial: string; matches: MeterSearchMatch[]; extraction: ManualReadingOcrExtraction; image_id: string; image_r2_key: string }
  | { kind: 'confirm'; match: MeterSearchMatch; extraction: ManualReadingOcrExtraction; image_id: string; image_r2_key: string }
  | { kind: 'submitting' }
  | { kind: 'submitted'; record_id: string }
  | { kind: 'error'; message: string };

const KNOWN_ATTR_HINTS: Record<string, string> = {
  Import_kWh: 'Cumulative imported energy (kWh)',
  Export_kWh: 'Cumulative exported energy (kWh)',
  Q1_kvarh: 'Quadrant 1 reactive energy (kvarh)',
  Q2_kvarh: 'Quadrant 2 reactive energy (kvarh)',
  Q3_kvarh: 'Quadrant 3 reactive energy (kvarh)',
  Q4_kvarh: 'Quadrant 4 reactive energy (kvarh)',
  kVAh: 'Apparent energy (kVAh)',
  Va: 'Phase A voltage (V)',
  Vb: 'Phase B voltage (V)',
  Vc: 'Phase C voltage (V)',
  waterLevel: 'Water meter cumulative reading',
  waterVolume_m3: 'Water volume (m³)',
};

export default function ReadingPage() {
  const { isAdmin } = useFieldRole();
  const [stage, setStage] = useState<Stage>({ kind: 'idle' });
  const [editedSerial, setEditedSerial] = useState('');
  const [editedReadings, setEditedReadings] = useState<Record<string, number>>({});
  const fileRef = useRef<HTMLInputElement>(null);
  // Bridge status — checked on mount + after every reset so the
  // "Read meter (NFC)" button reflects whether the local bridge is
  // actually running. Status check is best-effort (1.5s timeout) so
  // page render isn't blocked when no bridge is around.
  const [bridgeStatus, setBridgeStatus] = useState<NfcBridgeStatus>('offline');
  useEffect(() => {
    getBridgeStatus().then(setBridgeStatus);
  }, []);

  // NFC tap path: same staging as the OCR path, but the "extraction"
  // is built from the tap envelope rather than a Claude vision response.
  // Falls into the same `lookup → pick_match → confirm → submit` flow.
  async function handleNfcTap() {
    setStage({ kind: 'extracting', source: 'nfc' });
    try {
      const event = await tapAndAudit({ meterSerialHint: editedSerial || undefined });
      const synthetic: ManualReadingOcrExtraction = {
        meter_serial: event.meter.serial ?? null,
        readings: {
          waterVolume_m3: event.readings.waterVolume_m3 ?? null,
        },
        display_timestamp: event.timestamp,
        alarm_codes: event.alarms,
        confidence: { meter_serial: 0, readings_overall: 1 },
      };
      setEditedSerial(event.meter.serial ?? '');
      const r: Record<string, number> = {};
      if (typeof event.readings.waterVolume_m3 === 'number') {
        r.waterVolume_m3 = event.readings.waterVolume_m3;
      }
      setEditedReadings(r);
      setStage({
        kind: 'lookup',
        source: 'nfc',
        extraction: synthetic,
        image_id: '',
        image_r2_key: '',
        capturedReadings: r,
      });
    } catch (err) {
      setStage({ kind: 'error', message: (err as Error).message });
    }
  }

  async function handleFile(file: File | null) {
    if (!file) return;
    setStage({ kind: 'extracting', source: 'ocr' });
    try {
      const result = await api.ocrManualReading(file);
      const ext = result.extracted;
      // Pre-fill the editable state from the extraction
      setEditedSerial(ext.meter_serial || '');
      const r: Record<string, number> = {};
      for (const [k, v] of Object.entries(ext.readings || {})) {
        if (typeof v === 'number' && Number.isFinite(v)) r[k] = v;
      }
      setEditedReadings(r);
      setStage({
        kind: 'lookup',
        source: 'ocr',
        extraction: ext,
        image_id: result.image_id,
        image_r2_key: result.image_r2_key,
        capturedReadings: r,
      });
    } catch (err) {
      setStage({ kind: 'error', message: (err as Error).message });
    }
  }

  async function handleLookup() {
    if (stage.kind !== 'lookup' && stage.kind !== 'pick_match') return;
    const serial = (editedSerial || '').trim();
    if (!serial) {
      setStage({ kind: 'error', message: 'Enter a meter serial before looking up' });
      return;
    }
    const ext =
      stage.kind === 'lookup'
        ? { extraction: stage.extraction, image_id: stage.image_id, image_r2_key: stage.image_r2_key }
        : { extraction: stage.extraction, image_id: stage.image_id, image_r2_key: stage.image_r2_key };
    try {
      const result = await api.searchMetersBySerial(serial);
      const matches = result.data;
      if (matches.length === 0) {
        setStage({ kind: 'no_match', serial, ...ext });
        return;
      }
      if (matches.length === 1) {
        setStage({ kind: 'confirm', match: matches[0], ...ext });
        return;
      }
      // Multiple realms claim this serial — let the user pick.
      setStage({ kind: 'pick_match', serial, matches, ...ext });
    } catch (err) {
      setStage({ kind: 'error', message: (err as Error).message });
    }
  }

  function pickMatch(match: MeterSearchMatch) {
    if (stage.kind !== 'pick_match') return;
    setStage({
      kind: 'confirm',
      match,
      extraction: stage.extraction,
      image_id: stage.image_id,
      image_r2_key: stage.image_r2_key,
    });
  }

  async function handleSubmit() {
    if (stage.kind !== 'confirm') return;
    const match = stage.match;

    const readings: Record<string, number> = {};
    for (const [k, v] of Object.entries(editedReadings)) {
      if (typeof v === 'number' && Number.isFinite(v)) readings[k] = v;
    }
    if (Object.keys(readings).length === 0) {
      setStage({ kind: 'error', message: 'Enter at least one numeric reading before submitting' });
      return;
    }

    const body: ManualReadingSubmitBody = {
      meter_serial: editedSerial,
      asset_id: match.asset_id,
      realm: match.realm,
      readings,
      reading_timestamp:
        stage.extraction.display_timestamp || new Date().toISOString(),
      source: 'manual-ocr',
      source_payload: stage.extraction,
      image_id: stage.image_id,
      image_r2_key: stage.image_r2_key,
    };

    setStage({ kind: 'submitting' });
    try {
      const result = await api.submitManualReading(body);
      if (result.status === 'failed') {
        setStage({
          kind: 'error',
          message: `Submission failed: ${result.or_response_summary || 'unknown error'}`,
        });
        return;
      }
      setStage({ kind: 'submitted', record_id: result.record_id });
    } catch (err) {
      setStage({ kind: 'error', message: (err as Error).message });
    }
  }

  function reset() {
    setStage({ kind: 'idle' });
    setEditedSerial('');
    setEditedReadings({});
    if (fileRef.current) fileRef.current.value = '';
  }

  return (
    <div className="space-y-6">
      {/* Not wrapped in <Card>: bg-white/rounded-xl/shadow with no
          border-gray-* — doesn't match the Card-adoption trigger, and Card's
          own border would be a light-mode appearance change. */}
      <div className="bg-surface-raised rounded-xl shadow p-6">
        <h2 className="text-lg font-semibold text-content mb-1">Capture a manual reading</h2>
        <p className="text-sm text-content-muted mb-4">
          All submissions enter the approval queue while we tune the OCR.
          {isAdmin && ' As an admin, you can self-approve your own submissions from the Pending tab.'}
        </p>

        {stage.kind === 'idle' && (
          <div className="space-y-3">
            <UploadZone onFile={handleFile} fileRef={fileRef} />
            <NfcTapButton status={bridgeStatus} onTap={handleNfcTap} />
          </div>
        )}

        {stage.kind === 'extracting' && (
          <Status
            text={
              stage.source === 'nfc'
                ? 'Reading meter via NFC… hold the meter on the reader.'
                : 'Extracting reading from photo…'
            }
          />
        )}

        {stage.kind === 'lookup' && (
          <LookupStep
            source={stage.source}
            extraction={stage.extraction}
            capturedReadings={stage.capturedReadings}
            serial={editedSerial}
            onSerialChange={setEditedSerial}
            onContinue={handleLookup}
            onReset={reset}
          />
        )}

        {stage.kind === 'no_match' && (
          <NoMatchStep
            serial={stage.serial}
            isAdmin={isAdmin}
            onReset={reset}
          />
        )}

        {stage.kind === 'pick_match' && (
          <PickMatchStep
            serial={stage.serial}
            matches={stage.matches}
            onPick={pickMatch}
            onReset={reset}
          />
        )}

        {stage.kind === 'confirm' && (
          <ConfirmStep
            match={stage.match}
            serial={editedSerial}
            readings={editedReadings}
            displayTimestamp={stage.extraction.display_timestamp || ''}
            onChangeReading={(k, v) =>
              setEditedReadings(prev => ({ ...prev, [k]: v }))
            }
            onRemoveReading={k =>
              setEditedReadings(prev => {
                const next = { ...prev };
                delete next[k];
                return next;
              })
            }
            onAddReading={(k, v) =>
              setEditedReadings(prev => ({ ...prev, [k]: v }))
            }
            onSubmit={handleSubmit}
            onReset={reset}
          />
        )}

        {stage.kind === 'submitting' && <Status text="Submitting reading..." />}

        {stage.kind === 'submitted' && (
          <Submitted
            recordId={stage.record_id}
            isAdmin={isAdmin}
            onReset={reset}
          />
        )}

        {stage.kind === 'error' && (
          <ErrorBox message={stage.message} onReset={reset} />
        )}
      </div>
    </div>
  );
}

function NfcTapButton({
  status,
  onTap,
}: {
  status: NfcBridgeStatus;
  onTap: () => void;
}) {
  if (status === 'offline') {
    return (
      <div
        className="border border-border-token rounded-lg p-4 bg-surface-sunken text-center text-sm text-content-muted"
        title="The local NFC bridge isn't running. Install + start utilsync-nfc-bridge to enable NFC reads."
      >
        <span className="inline-flex items-center gap-1.5">
          {/* Neutral "off" dot — same bg-stone-500/dark:bg-stone-400 pair
              group E used for Tenants.tsx's inactive-status dot. */}
          <span className="inline-block w-2 h-2 rounded-full bg-stone-500 dark:bg-stone-400" />
          NFC bridge offline
        </span>
        <span className="block text-[11px] text-content-muted mt-0.5">
          Photo / manual entry below still works.
        </span>
      </div>
    );
  }
  if (status === 'unauthenticated') {
    return (
      // Promoted to text-content (not text-content-muted): this tells the
      // technician exactly what to do to unblock NFC capture (refresh + sign
      // in) — actionable guidance, not passive chrome.
      <div className="border border-border-token rounded-lg p-4 bg-surface-sunken text-center text-sm text-content">
        NFC bridge rejected the session token — refresh the page and sign in again.
      </div>
    );
  }
  return (
    <button
      type="button"
      onClick={onTap}
      // hover:bg-emerald-50/40 is a fixed light wash — a glaring patch on the
      // dark card on hover. dark:hover:bg-emerald-950/40 keeps the "success
      // family" hue without the flash.
      className="w-full border-2 border-dashed border-emerald-300 rounded-lg p-6 text-center hover:border-emerald-500 hover:bg-emerald-50/40 dark:hover:bg-emerald-950/40 transition-colors"
    >
      {/* text-emerald-800/700 both fail badly against the dark card
          (1.97:1 / 2.77:1, computed by hand). dark:text-emerald-400 (7.89:1)
          / dark:text-emerald-300 (9.95:1) restore legibility. This is the
          primary NFC capture control — a colour-only-adjacent affordance
          (green = "ready to tap") that must read clearly at night. */}
      <p className="text-emerald-800 dark:text-emerald-400 font-medium inline-flex items-center gap-2">
        <span className="inline-block w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
        Read meter (NFC)
      </p>
      <p className="text-sm text-emerald-700/70 dark:text-emerald-300 mt-1">
        Tap an Axioma QW1 against the reader to capture cumulative volume.
      </p>
    </button>
  );
}

function UploadZone({
  onFile,
  fileRef,
}: {
  onFile: (f: File | null) => void;
  fileRef: React.RefObject<HTMLInputElement>;
}) {
  return (
    <div
      className="border-2 border-dashed border-border-token rounded-lg p-8 text-center hover:border-primary-400 hover:bg-surface-sunken transition-colors cursor-pointer"
      onClick={() => fileRef.current?.click()}
      onDragOver={e => e.preventDefault()}
      onDrop={e => {
        e.preventDefault();
        onFile(e.dataTransfer.files?.[0] || null);
      }}
    >
      {/* Camera-capture input: styling only, capture="environment"/onFile
          logic untouched. */}
      <input
        ref={fileRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif"
        capture="environment"
        className="hidden"
        onChange={e => onFile(e.target.files?.[0] || null)}
      />
      <p className="text-content font-medium">Tap to take a photo, or drop an image here</p>
      <p className="text-sm text-content-muted mt-1">JPEG / PNG / WEBP / GIF, up to 10MB</p>
    </div>
  );
}

function Status({ text }: { text: string }) {
  return (
    <div className="flex items-center gap-3 py-6">
      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary-600" />
      <p className="text-content">{text}</p>
    </div>
  );
}

function LookupStep({
  source,
  extraction,
  capturedReadings,
  serial,
  onSerialChange,
  onContinue,
  onReset,
}: {
  source: CaptureSource;
  extraction: ManualReadingOcrExtraction;
  capturedReadings: Record<string, number>;
  serial: string;
  onSerialChange: (v: string) => void;
  onContinue: () => void;
  onReset: () => void;
}) {
  const conf = extraction.confidence?.meter_serial;
  const readingPairs = Object.entries(capturedReadings);
  return (
    <div className="space-y-4">
      {readingPairs.length > 0 && (
        // Source-colour box (emerald=NFC, blue=OCR) is a genuine colour
        // signal distinguishing capture method — both header text colours
        // fail badly against the dark card (emerald-800 1.97:1, blue-800
        // 1.74:1, computed by hand). dark:text-emerald-300/dark:text-blue-300
        // (9.95:1 / 8.41:1) restore legibility without losing the hue split.
        <div className={`rounded-lg border p-3 ${source === 'nfc' ? 'bg-emerald-50 dark:bg-emerald-950 border-emerald-200 dark:border-emerald-800' : 'bg-blue-50 dark:bg-blue-950 border-blue-200 dark:border-blue-800'}`}>
          <p className={`text-xs font-semibold uppercase tracking-wide ${source === 'nfc' ? 'text-emerald-800 dark:text-emerald-300' : 'text-blue-800 dark:text-blue-300'}`}>
            {source === 'nfc' ? 'Captured via NFC' : 'Extracted from photo'}
          </p>
          <ul className="mt-1.5 space-y-0.5">
            {readingPairs.map(([k, v]) => (
              <li key={k} className="text-sm font-mono text-content">
                <span className="text-content-muted">{k}:</span> {v}
                {KNOWN_ATTR_HINTS[k] && (
                  <span className="ml-2 text-[11px] font-sans text-content-muted">— {KNOWN_ATTR_HINTS[k]}</span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
      <div>
        <label className="block text-sm font-medium text-content mb-1">Meter serial</label>
        <input
          type="text"
          value={serial}
          onChange={e => onSerialChange(e.target.value)}
          className="w-full px-3 py-2 border border-border-token rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          placeholder={source === 'nfc' ? 'Type the serial from the meter face' : 'e.g. 18129117'}
        />
        {source === 'nfc' && (
          <p className="text-xs text-content-muted mt-1">
            NFC can't read the meter serial from passive memory — type it from the meter face. The reading above is already captured.
          </p>
        )}
        {source === 'ocr' && conf !== undefined && (
          <p className="text-xs text-content-muted mt-1">
            OCR confidence: {(conf * 100).toFixed(0)}%
          </p>
        )}
      </div>
      <div className="flex gap-2">
        <button
          onClick={onContinue}
          className="px-4 py-2 bg-accent text-accent-fg rounded-lg hover:opacity-90 disabled:opacity-50"
          disabled={!serial.trim()}
        >
          Look up meter
        </button>
        <button onClick={onReset} className="px-4 py-2 text-content-muted hover:text-content">
          Start over
        </button>
      </div>
    </div>
  );
}

function NoMatchStep({
  serial,
  isAdmin,
  onReset,
}: {
  serial: string;
  isAdmin: boolean;
  onReset: () => void;
}) {
  return (
    // bg-yellow-50/text-yellow-800/900 is a fixed literal pair — invisible-
    // on-dark if left alone. dark:bg-yellow-900/dark:border-yellow-800/
    // dark:text-yellow-100 (8.07:1, computed by hand) — same pattern as the
    // established red/amber/blue severity-box treatment. This is the "meter
    // not found" state the technician must act on.
    <div className="rounded-lg bg-yellow-50 dark:bg-yellow-900 border border-yellow-200 dark:border-yellow-800 p-4">
      <h3 className="font-medium text-yellow-900 dark:text-yellow-100">Meter not found</h3>
      <p className="text-sm text-yellow-800 dark:text-yellow-100 mt-1">
        No commissioned meter named <code className="bg-yellow-100 dark:bg-yellow-800 px-1.5 py-0.5 rounded">{serial}</code> exists in any realm.
      </p>
      <p className="text-sm text-yellow-800 dark:text-yellow-100 mt-2">
        {isAdmin
          ? 'Commission this meter first. (Hint: the photo you just uploaded is already in R2 — note the serial and switch to the Commission tab.)'
          : 'An admin needs to commission this meter before readings can be submitted. Flag it with whoever runs your commissioning.'}
      </p>
      <button
        onClick={onReset}
        className="mt-3 px-3 py-1.5 text-sm bg-white dark:bg-yellow-950 border border-yellow-300 dark:border-yellow-700 rounded-lg hover:bg-yellow-100 dark:hover:bg-yellow-900 dark:text-yellow-100"
      >
        Try a different photo
      </button>
    </div>
  );
}

function PickMatchStep({
  serial,
  matches,
  onPick,
  onReset,
}: {
  serial: string;
  matches: MeterSearchMatch[];
  onPick: (m: MeterSearchMatch) => void;
  onReset: () => void;
}) {
  return (
    <div className="space-y-3">
      {/* Fix-wave B: routed onto ui/Alert — same bg-blue-50/900,
          text-blue-800/100 shape as Alert's `info` tone (the box's own
          text-blue-900 light was marginally darker than Alert's text-blue-800;
          both clear AA comfortably, see fix-wave-b-report.md). */}
      <Alert tone="info">
        <p className="text-sm">
          Multiple realms have a meter named{' '}
          <code className="bg-blue-100 dark:bg-blue-800 px-1.5 py-0.5 rounded">{serial}</code>. Pick the one
          you're capturing.
        </p>
      </Alert>
      <ul className="divide-y divide-border-token border border-border-token rounded-lg">
        {matches.map(m => (
          <li key={`${m.realm}:${m.asset_id}`} className="p-3 flex items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="text-sm font-medium">{m.asset_type}</p>
              {/* Promoted to text-content (not text-content-muted): realm +
                  asset id are the disambiguating identifiers for picking the
                  right meter — same reasoning as PendingPage's MeterPicker. */}
              <p className="text-xs text-content">
                realm <code className="bg-border-token px-1 rounded">{m.realm}</code>
                {' '}— asset <code className="bg-border-token px-1 rounded font-mono text-[10px]">{m.asset_id}</code>
              </p>
              {m.parent_path && m.parent_path.length > 0 ? (
                <p className="text-xs text-content-muted mt-0.5">
                  {m.parent_path.map((node, i) => (
                    <span key={node.id}>
                      {i > 0 && <span className="text-content-muted mx-1">›</span>}
                      <span className="text-content">{node.name}</span>
                      <span className="text-content-muted"> ({node.type})</span>
                    </span>
                  ))}
                </p>
              ) : (
                <p className="text-xs text-content-muted mt-0.5 italic">no parent (top-level asset)</p>
              )}
            </div>
            <button
              onClick={() => onPick(m)}
              className="px-3 py-1.5 text-sm bg-accent text-accent-fg rounded hover:opacity-90"
            >
              Pick this one
            </button>
          </li>
        ))}
      </ul>
      <button onClick={onReset} className="text-sm text-content-muted hover:text-content">
        Start over
      </button>
    </div>
  );
}

function ConfirmStep({
  match,
  serial,
  readings,
  displayTimestamp,
  onChangeReading,
  onRemoveReading,
  onAddReading,
  onSubmit,
  onReset,
}: {
  match: MeterSearchMatch;
  serial: string;
  readings: Record<string, number>;
  displayTimestamp: string;
  onChangeReading: (k: string, v: number) => void;
  onRemoveReading: (k: string) => void;
  onAddReading: (k: string, v: number) => void;
  onSubmit: () => void;
  onReset: () => void;
}) {
  const [newAttr, setNewAttr] = useState('');
  const [newVal, setNewVal] = useState('');

  return (
    <div className="space-y-4">
      {/* Confirms exactly which meter/realm/asset the reading is about to be
          committed against — a misread here misroutes a reading, so it must
          stay legible. Fix-wave B: routed onto ui/Alert (same bg-green-50/900
          shape this box already used; Alert's text-green-900 is marginally
          higher-contrast than this box's own dark:text-green-100 — no
          regression). */}
      <Alert tone="success">
        <p className="text-sm">
          Matched <code className="bg-green-100 dark:bg-green-800 px-1.5 py-0.5 rounded">{serial}</code>
          {' '}({match.asset_type}) in realm{' '}
          <code className="bg-green-100 dark:bg-green-800 px-1.5 py-0.5 rounded">{match.realm}</code>
          {' '}— asset <code className="text-xs font-mono">{match.asset_id}</code>
        </p>
      </Alert>

      <div>
        <h3 className="text-sm font-medium text-content mb-2">Readings to submit</h3>
        {Object.keys(readings).length === 0 && (
          <p className="text-sm text-content-muted">No readings extracted. Add at least one below.</p>
        )}
        <div className="space-y-2">
          {Object.entries(readings).map(([k, v]) => (
            <div key={k} className="flex gap-2 items-center">
              <div className="flex-1">
                {/* Promoted to text-content (not text-content-muted): this is
                    the attribute name for a reading about to be submitted —
                    a technician needs to read it correctly before
                    confirming. */}
                <p className="text-sm font-medium text-content">{k}</p>
                <p className="text-xs text-content-muted">{KNOWN_ATTR_HINTS[k] || 'Custom attribute'}</p>
              </div>
              <input
                type="number"
                step="any"
                value={v}
                onChange={e => {
                  const n = parseFloat(e.target.value);
                  if (Number.isFinite(n)) onChangeReading(k, n);
                }}
                className="w-40 px-3 py-1.5 border border-border-token rounded text-right"
              />
              <button
                onClick={() => onRemoveReading(k)}
                // text-red-600 alone is 3.14:1 against the dark card — fails.
                // dark:text-red-400 (5.48:1) / dark:hover:text-red-300.
                className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 text-sm px-2"
                title="Remove this reading"
              >
                Remove
              </button>
            </div>
          ))}
        </div>
      </div>

      <div className="border-t border-border-token pt-4">
        <h3 className="text-sm font-medium text-content mb-2">Add another attribute</h3>
        <div className="flex gap-2">
          <input
            type="text"
            placeholder="Attribute name"
            value={newAttr}
            onChange={e => setNewAttr(e.target.value)}
            className="flex-1 px-3 py-1.5 border border-border-token rounded"
          />
          <input
            type="number"
            step="any"
            placeholder="Value"
            value={newVal}
            onChange={e => setNewVal(e.target.value)}
            className="w-32 px-3 py-1.5 border border-border-token rounded text-right"
          />
          <button
            onClick={() => {
              const n = parseFloat(newVal);
              if (newAttr.trim() && Number.isFinite(n)) {
                onAddReading(newAttr.trim(), n);
                setNewAttr('');
                setNewVal('');
              }
            }}
            // bg-gray-200/hover:bg-gray-300 has no exact two-tier token
            // equivalent — border-token is already the darkest neutral tier
            // short of a full surface, so the hover step darkens via opacity
            // instead of a nonexistent third tier.
            className="px-3 py-1.5 bg-border-token rounded hover:opacity-80"
          >
            Add
          </button>
        </div>
      </div>

      {/* Promoted to text-content (not text-content-muted): this is the
          actual reading timestamp being submitted, not decorative chrome —
          worth a technician's attention before confirming. */}
      <div className="text-sm text-content">
        <p>
          Reading timestamp:{' '}
          <code>{displayTimestamp || `now (${new Date().toISOString()})`}</code>
        </p>
      </div>

      <div className="flex gap-2 pt-2">
        <button
          onClick={onSubmit}
          className="px-4 py-2 bg-accent text-accent-fg rounded-lg hover:opacity-90 disabled:opacity-50"
          disabled={Object.keys(readings).length === 0}
        >
          Submit for approval
        </button>
        <button onClick={onReset} className="px-4 py-2 text-content-muted hover:text-content">
          Start over
        </button>
      </div>
    </div>
  );
}

function Submitted({
  recordId,
  isAdmin,
  onReset,
}: {
  recordId: string;
  isAdmin: boolean;
  onReset: () => void;
}) {
  return (
    // Terminal capture-state indicator (success): a colour-only-adjacent
    // signal the brief specifically calls out. Fix-wave B: routed onto
    // ui/Alert — same bg-green-50/900 shape this box already used (the
    // "Capture another" button stays its own literal green so it keeps
    // matching the panel it sits in; see fix-wave-b-report.md).
    <Alert tone="success" title="Reading queued for approval">
      <p className="text-sm mt-1">
        Record ID: <code className="bg-green-100 dark:bg-green-800 px-1.5 py-0.5 rounded text-xs">{recordId}</code>
      </p>
      {isAdmin && (
        <p className="text-sm mt-1">
          Switch to the Pending tab to review and approve.
        </p>
      )}
      <button
        onClick={onReset}
        className="mt-3 px-3 py-1.5 text-sm bg-white dark:bg-green-950 border border-green-300 dark:border-green-700 rounded-lg hover:bg-green-100 dark:hover:bg-green-900 dark:text-green-100"
      >
        Capture another
      </button>
    </Alert>
  );
}

function ErrorBox({ message, onReset }: { message: string; onReset: () => void }) {
  return (
    // Terminal capture-state indicator (failure) — the other half of the
    // green/red pair above. Fix-wave B: routed onto ui/Alert — same
    // bg-red-50/900 shape this box already used. This carries the actual
    // error message, so it must stay legible (Alert's own text-red-800/100
    // clears AA the same as this box's own values did — see
    // fix-wave-b-report.md).
    <Alert tone="danger" title="Something went wrong">
      <p className="text-sm mt-1">{message}</p>
      <button
        onClick={onReset}
        className="mt-3 px-3 py-1.5 text-sm bg-white dark:bg-red-950 border border-red-300 dark:border-red-700 rounded-lg hover:bg-red-100 dark:hover:bg-red-900 dark:text-red-100"
      >
        Start over
      </button>
    </Alert>
  );
}
